import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Send, Loader2 } from "lucide-react";
import { PHONE, PHONE_LINK, BOOKING_SERVICES, Reveal, Eyebrow } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", service: "", preferred_date: "", message: "" };

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#EADFC6] bg-white focus:border-[#A6823B] focus:ring-4 focus:ring-[#A6823B]/10 outline-none text-[#2A2521] transition-all";

const BookingForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!gdpr) {
      toast.error("Te rugăm să accepți prelucrarea datelor (GDPR).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Cerere trimisă! Te sunăm noi în cel mai scurt timp.");
      setForm(EMPTY);
      setGdpr(false);
    } catch {
      toast.error("A apărut o eroare. Sună-ne direct la " + PHONE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#FBF7EF]" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#2A2521] mt-3">
            Discută cu un consultant Medas
          </h2>
          <p className="text-[#6B5C43] mt-4 text-base md:text-lg">
            Cel mai rapid mod de a te programa este un simplu telefon. Sau lasă-ne datele și te sunăm noi.
          </p>
          <a
            href={PHONE_LINK}
            data-testid="booking-phone-button"
            className="mt-8 inline-flex items-center gap-3 bg-[#A6823B] hover:bg-[#8A6B2E] text-white font-semibold text-lg px-7 py-4 rounded-full shadow-xl shadow-[#A6823B]/25 transition-all hover:-translate-y-0.5"
          >
            <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center pulse-ring">
              <Phone className="w-4 h-4" />
            </span>
            {PHONE}
          </a>
          <div className="mt-8 p-5 rounded-2xl bg-white border border-[#EADFC6]">
            <p className="text-sm text-[#8A7A5E]">
              Datele tale sunt procesate confidențial, conform Regulamentului GDPR (UE) 2016/679. Nu le folosim în scopuri de marketing fără acordul tău.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="bg-white rounded-2xl border border-[#EADFC6] p-6 lg:p-8 space-y-4 shadow-lg shadow-[#2A2521]/5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Nume complet *</label>
                <input required value={form.name} onChange={set("name")} data-testid="booking-form-name" className={inputCls} placeholder="Ion Popescu" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Telefon *</label>
                <input required value={form.phone} onChange={set("phone")} data-testid="booking-form-phone" className={inputCls} placeholder="07xx xxx xxx" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Email</label>
                <input type="email" value={form.email} onChange={set("email")} data-testid="booking-form-email" className={inputCls} placeholder="email@exemplu.ro" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Serviciu</label>
                <select value={form.service} onChange={set("service")} data-testid="booking-form-service-select" className={inputCls}>
                  <option value="">Alege...</option>
                  {BOOKING_SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Dată preferată</label>
              <input type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid="booking-form-date" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#4A3F2E] mb-1.5 block">Mesaj</label>
              <textarea rows={3} value={form.message} onChange={set("message")} data-testid="booking-form-message" className={inputCls} placeholder="Cum te putem ajuta?" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={gdpr}
                onChange={(e) => setGdpr(e.target.checked)}
                data-testid="booking-form-gdpr-checkbox"
                className="mt-1 w-4 h-4 accent-[#A6823B]"
              />
              <span className="text-sm text-[#8A7A5E]">
                Sunt de acord cu prelucrarea datelor mele personale în scopul programării, conform{" "}
                <a href="/politica-gdpr" className="text-[#A6823B] underline">politicii GDPR</a>. *
              </span>
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="booking-form-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#A6823B] hover:bg-[#8A6B2E] disabled:opacity-60 text-white font-semibold px-5 py-3.5 rounded-full transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Trimite cererea de programare
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default BookingForm;
